import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SearchRounded from '@mui/icons-material/SearchRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import MusicNoteRounded from '@mui/icons-material/MusicNoteRounded';
import { useJamRoom } from '../hooks/useJamRoom.jsx';
import { fmtDuration } from '../lib/format.js';
import TrackCover from './TrackCover.jsx';

/**
 * Buscador en vivo (debounce 400 ms) contra `search_track`:
 * solo música de YouTube Music; estados de carga/error/vacío y añadir a la cola.
 */
export default function SearchPanel() {
  const jam = useJamRoom();
  const searchTracks = jam.search;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null: aun sin busqueda
  const [status, setStatus] = useState('idle'); // idle | loading | error | done
  const [errorMsg, setErrorMsg] = useState('');
  const [adding, setAdding] = useState(null);
  const [nonce, setNonce] = useState(0); // para el boton Reintentar
  const seqRef = useRef(0);
  const inQueue = useMemo(() => new Set((jam.queue ?? []).map((t) => t.videoId)), [jam.queue]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      setStatus('idle');
      return undefined;
    }
    const timer = setTimeout(() => {
      const seq = ++seqRef.current;
      setStatus('loading');
      searchTracks(q)
        .then((res) => {
          if (seqRef.current !== seq) return;
          setResults(res.tracks ?? []);
          setStatus('done');
        })
        .catch((err) => {
          if (seqRef.current !== seq) return;
          setErrorMsg(err.message || 'La búsqueda falló.');
          setStatus('error');
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [query, nonce, searchTracks]);

  async function add(track) {
    setAdding(track.videoId);
    try {
      await jam.addToQueue(track);
    } catch (err) {
      jam.pushToast(err.message || 'No se pudo añadir la canción', 'error');
    } finally {
      setAdding((current) => (current === track.videoId ? null : current));
    }
  }

  return (
    <Paper
      elevation={0}
      component="section"
      aria-label="Buscador de música"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRadius: '24px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(30,31,32,0.72)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Typography variant="h6">Buscar</Typography>
        <Chip
          size="small"
          variant="outlined"
          icon={<MusicNoteRounded />}
          label="YouTube Music"
          sx={{ color: 'text.secondary', borderColor: 'divider' }}
        />
      </Box>

      <Box sx={{ px: 2, pb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Busca en YouTube Music…"
          spellCheck={false}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: { xs: 'visible', md: 'auto' }, px: 1, py: 1 }}>
        {status === 'idle' && (
          <Stack
            spacing={1.5}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 200,
              px: 3,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <SearchRounded sx={{ fontSize: 36, opacity: 0.65 }} />
            <Typography variant="body2">
              Busca tu música y pulsa + para añadirla a la cola colaborativa.
            </Typography>
          </Stack>
        )}

        {status === 'loading' && (
          <Stack spacing={0.5} sx={{ py: 1 }} aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <Stack key={i} direction="row" spacing={1.5} sx={{ alignItems: 'center', px: 1, py: 0.75 }}>
                <Skeleton variant="rounded" width={36} height={36} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="70%" height={14} />
                  <Skeleton width="40%" height={12} />
                </Box>
              </Stack>
            ))}
          </Stack>
        )}

        {status === 'error' && (
          <Box sx={{ p: 1.5 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => setNonce((v) => v + 1)}>
                  Reintentar
                </Button>
              }
            >
              {errorMsg}
            </Alert>
          </Box>
        )}

        {status === 'done' && results?.length === 0 && (
          <Stack spacing={1} sx={{ alignItems: 'center', justifyContent: 'center', minHeight: 200, px: 3 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Sin resultados para “{query.trim()}”.
            </Typography>
          </Stack>
        )}

        {status === 'done' &&
          results?.length > 0 &&
          results.map((track) => {
            const queued = inQueue.has(track.videoId);
            return (
              <Box
                key={track.videoId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1,
                  py: 0.75,
                  borderRadius: '14px',
                  transition: 'background-color .15s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <TrackCover src={track.thumbnail} alt={track.title} size="sm" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                    {track.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {track.artist}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}
                >
                  {fmtDuration(track.durationSec)}
                </Typography>
                <Tooltip title={queued ? 'Ya está en la cola' : 'Añadir a la cola'}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={queued || adding === track.videoId}
                      onClick={() => add(track)}
                      color={queued ? 'success' : 'primary'}
                      aria-label={queued ? 'Ya está en la cola' : 'Añadir a la cola'}
                      sx={{
                        bgcolor: queued ? 'transparent' : 'rgba(168,199,250,0.10)',
                        '&:hover': { bgcolor: 'rgba(168,199,250,0.20)' },
                      }}
                    >
                      {queued ? (
                        <CheckRounded fontSize="small" />
                      ) : adding === track.videoId ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AddRounded fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
      </Box>
    </Paper>
  );
}
